import React, { useEffect, useMemo, useState } from 'react';
import { MapPin, Layers, Target, ShieldCheck, Lock } from 'lucide-react';
import { useReveal } from '@/lib/useReveal';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/* ── Representative Mandates ───────────────────────────────────────────────
 * NDA-safe institutional tombstones — no borrower, lender, asset, or address
 * data. Code prefix (RE/CORP/STRUCT) + jurisdiction + sector + size band +
 * structure + outcome only. Conforms to GP CAP Public Site DS v1:
 *   - navy surface (gpc-section--alt)
 *   - single card system (gpc-card)
 *   - pill filter chips, primary CTA absent (this section is descriptive)
 *   - single section-level scroll reveal
 *   - active-filter transition on specific properties only
 */

const DEFAULT_LIMIT = 6;

const CAPITAL_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 're', label: 'Real estate', match: (c) => /^RE\b/i.test(c || '') },
  { key: 'corp', label: 'Corporate & growth', match: (c) => /^CORP\b/i.test(c || '') },
  { key: 'struct', label: 'Structured & special', match: (c) => /^(STRUCT|SS|OTHER)\b/i.test(c || '') || /special|structur/i.test(c || '') },
];

const TombstoneCard = ({ t }) => (
  <div
    className="gpc-card group flex flex-col gap-3 relative"
    data-testid={`selected-transaction-${(t.code || '').replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
  >
    <div className="flex items-center justify-between">
      <span className="gpc-num text-[10px]" style={{ color: 'var(--gpc-muted)', letterSpacing: '0.2em' }}>
        {t.code}
      </span>
      <span
        className="gpc-num text-[13px] font-medium"
        style={{ color: 'var(--gpc-ink-soft)' }}
      >
        {t.sizeBand}
      </span>
    </div>

    <h3 className="gpc-h3" style={{ fontSize: '15px' }}>{t.sector}</h3>

    <div className="space-y-1.5 text-[12px]" style={{ color: 'var(--gpc-muted)', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div className="flex items-center gap-2">
        <MapPin className="w-3 h-3 shrink-0" />
        <span>{t.jurisdiction}</span>
      </div>
      <div className="flex items-center gap-2">
        <Target className="w-3 h-3 shrink-0" />
        <span>{t.structure}</span>
      </div>
      <div className="flex items-center gap-2">
        <Layers className="w-3 h-3 shrink-0" />
        <span>{t.status || 'Mandate'}</span>
      </div>
    </div>

    <p
      className="text-[13px] mt-1"
      style={{ color: 'var(--gpc-ink)', opacity: 0.86, fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.55 }}
    >
      {t.outcome}
    </p>

    {/* NDA cue — revealed on hover */}
    <div
      className="mt-1 flex items-center gap-1.5 text-[10px]"
      style={{
        color: 'var(--gpc-muted)',
        opacity: 0,
        transition: 'opacity 0.2s ease',
        fontFamily: 'Space Grotesk, system-ui, sans-serif',
        letterSpacing: '0.08em',
      }}
      data-testid="selected-tx-nda-cue"
      onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
      onMouseLeave={(e) => { e.currentTarget.style.opacity = '0'; }}
    >
      <Lock className="w-3 h-3" style={{ color: 'var(--gpc-gold)' }} />
      <span>NDA-safe summary · detail on request</span>
    </div>
  </div>
);

export const SelectedTransactions = () => {
  const [items, setItems] = useState([]);
  const [source, setSource] = useState('pending');
  const [filter, setFilter] = useState('all');
  const [ref, visible] = useReveal({ threshold: 0.1 });

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/public/selected-transactions?limit=${DEFAULT_LIMIT}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        if (cancelled) return;
        if (Array.isArray(d?.items)) {
          setItems(d.items.slice(0, DEFAULT_LIMIT));
          setSource('api');
        }
      })
      .catch(() => { if(!cancelled)setSource('unavailable'); });
    return () => { cancelled = true; };
  }, []);

  const visibleItems = useMemo(() => {
    if (filter === 'all') return items;
    const def = CAPITAL_FILTERS.find((f) => f.key === filter);
    if (!def || !def.match) return items;
    return items.filter((t) => def.match(t.code));
  }, [items, filter]);

  return (
    <section
      ref={ref}
      className="gpc-section"
      data-testid="selected-transactions-section"
      data-source={source}
    >
      <div className={`gpc-container gpc-reveal ${visible ? 'is-visible' : ''}`}>
        <div className="max-w-3xl mb-10">
          <p className="gpc-eyebrow mb-4">Private Capital · Representative mandates</p>
          <h2 className="gpc-h2 mb-3">
            Selected <span style={{ color: 'var(--gpc-gold)', fontStyle: 'italic' }}>transactions.</span>
          </h2>
          <p className="gpc-body max-w-2xl">
            Anonymised examples of mandates executed or introduced through the platform. Borrower, sponsor and capital-partner identities, addresses and specific asset details are intentionally omitted.
          </p>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="selected-transactions-filters">
          {CAPITAL_FILTERS.map((f) => {
            const active = filter === f.key;
            const count =
              f.key === 'all'
                ? items.length
                : items.filter((t) => f.match && f.match(t.code)).length;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                aria-pressed={active}
                data-testid={`selected-tx-filter-${f.key}`}
                className="px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5"
                style={{
                  background: active ? 'var(--gpc-ink)' : 'transparent',
                  color: active ? 'var(--gpc-paper-2)' : 'var(--gpc-ink)',
                  border: '1px solid ' + (active ? 'var(--gpc-ink)' : 'var(--gpc-line-strong)'),
                  fontFamily: 'Inter, system-ui, sans-serif',
                  transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease',
                }}
              >
                <span>{f.label}</span>
                <span
                  className="gpc-num"
                  style={{
                    color: active ? 'var(--gpc-paper-2)' : 'var(--gpc-muted)',
                    fontSize: '10px',
                    letterSpacing: '0.06em',
                  }}
                >· {count}</span>
              </button>
            );
          })}
        </div>

        {visibleItems.length === 0 ? (
          <div
            className="gpc-card text-[13px]"
            style={{ color: 'var(--gpc-muted)' }}
            data-testid="selected-transactions-empty"
          >
            Published mandate information is not currently available here. Contact the desk for institutional enquiries.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {visibleItems.map((t) => <TombstoneCard key={t.id || t.code} t={t} />)}
          </div>
        )}

        <div
          className="mt-8 flex items-center gap-2 text-[11px]"
          style={{
            color: 'var(--gpc-muted)',
            fontFamily: 'Space Grotesk, system-ui, sans-serif',
            letterSpacing: '0.04em',
          }}
        >
          <ShieldCheck className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--gpc-gold)' }} />
          <span>
            All transaction detail has been anonymised. No GP Capital client, counterparty, address or confidential term is disclosed. Structures, sizes and outcomes are representative of the mandate envelope.
          </span>
        </div>
      </div>
    </section>
  );
};

export default SelectedTransactions;
